import withAuthentication from "@/hoc/withAuthentication";
import css from "./.module.scss";
import { InitialResolver } from "@/lib/use.middlewares";
import { AuthResolver } from "@/lib/serverOnly";
import Image from "next/image";
import { Button } from "@aqar-app/react-ui";
import { CloudfrontImageLoader, financialFormat } from "@/lib/helpers";
import Link from "next/link";
import { useAuthState } from "@/contexts/auth/auth.provider";
import { useLazyQuery, useQuery } from "@apollo/client";
import { SOV_CAMPAIGN_LIST } from "@/graphql/queries/sov";
import dayjs from "dayjs";
import CloudFrontLoader from "@/common/cloudFrontLoader";
import { useRouter } from "next/router";
import SEO from "../../../components/seo/SEO";
import { STATIC_PAGES_META } from "../../../lib/metaDataHelpers";
import { useEffect } from "react";
import {
  getFeaturedAdItemObject,
  trackFeaturedAdAddToCart,
  trackStartFeaturedAdClickInteraction,
  trackViewFeaturedAd,
} from "@/lib/tracking/trackAdPromotionEvents";
import { GET_LISTING } from "@/graphql/queries/listing";
import { usePath } from "@/contexts/path/path";

function CampaignCard({ campaign }) {
  return (
    <Link href={`/user/campaigns/${campaign.id}`}>
      <div className={css.campaign}>
        <div>
          <h3>{campaign.listing_title}</h3>
          <p className={css.status}>
            <span className={css[campaign.status]} />
          </p>
          <div>
            <p>
              <span> الميزانية اليومية (ريال):</span>
              <strong>{financialFormat(campaign.daily_budget * 0.01)}</strong>
            </p>
            <p>
              <span>تاريخ الانتهاء:</span>
              <span>
                {campaign.end_datetime
                  ? dayjs(campaign.end_datetime).format("DD/MM/YY")
                  : "حتى إغلاق الإعلان"}
              </span>
            </p>
            <p>
              <span>تاريخ التفعيل:</span>
              <span>{dayjs(campaign.start_datetime).format("DD/MM/YY")}</span>
            </p>
          </div>
        </div>
        <div>
          {campaign.listing_imgs?.[0] ? (
            <Image
              fill
              src={campaign.listing_imgs[0]}
              loader={CloudFrontLoader}
              alt={campaign.listing_imgs[0]}
            />
          ) : null}
          <p className={css.status}>
            <span className={css[campaign.status]} />
          </p>
        </div>
      </div>
    </Link>
  );
}

export function UserCampaigns() {
  const { token } = useAuthState();
  const { data } = useQuery(SOV_CAMPAIGN_LIST, { context: { token } });
  return (
    <div className={css.campaignList}>
      {data?.SovCampaign?.list?.list.map((campaign) => (
        <CampaignCard campaign={campaign} />
      ))}
    </div>
  );
}

const Campaigns = withAuthentication(() => {
  const { previousPath } = usePath();
  const router = useRouter();

  const listingType = router.query.listing_type
    ? decodeURIComponent(router.query.listing_type)
    : undefined;
  const category = router.query.category
    ? decodeURIComponent(router.query.category)
    : undefined;

  const [getListing, { data }] = useLazyQuery(GET_LISTING, {
    variables: { id: router.query.id },
  });

  useEffect(() => {
    getListing().then(({ data }) => {
      const listing = data?.Web?.listing;
      if (listing) {
        trackViewFeaturedAd({
          listingType,
          category,
          item: getFeaturedAdItemObject({
            listing,
            index: 1,
            categoriesDictionary: {},
            categoryName: category,
          }),
          listingId: router.query.id,
        });
      }
    });
  }, []);

  const listing = data?.Web?.listing;

  return (
    <div className={css.index}>
      <SEO {...STATIC_PAGES_META.campaigns} />

      <h3>تمييز الإعلان</h3>

      <div className={css.container}>
        <p>
          حول إعلانك إلى مميز و سرع من عملية البيع أو التأجير عن طريق خطوات سهلة
          و بسيطة
        </p>

        <div>
          <div className={css.stepper}>
            <div className={css.o}>
              <div className={css.number} />
              <div className={css.line} />
            </div>
            <div className={css.o}>
              <div className={css.number} />
              <div className={css.line} />
            </div>
            <div className={css.o}>
              <div className={css.number} />
              <div className={css.line} />
            </div>
          </div>
          <div className={css.texts}>
            <div>
              <p>أختر إعلان من قائمة إعلاناتك</p>
            </div>
            <div>
              <p>حدد الميزانية اليومية</p>
            </div>
            <div>
              <p>أختر تاريخ انتهاء تمييز الإعلان</p>
            </div>
          </div>
        </div>

        <h3>فوائد تمييز الإعلان</h3>

        <div className={css.benefits}>
          <div className={css.benefit}>
            <div className={css.text}>
              <h4>أيقونة خاصة</h4>
              <p>
                تحصل الإعلانات المميزة على أيقونة خاصة و مظهر فريد يميزها عن
                بقية الإعلانات.
              </p>
            </div>
            <div className={css.image}>
              <Image
                alt="campaign3"
                width={150}
                height={150}
                loader={CloudfrontImageLoader}
                src={`000000000_1707660584002.png`}
              />
            </div>
          </div>
          <div className={css.benefit}>
            <div className={css.text}>
              <h4>ظهور مميز</h4>
              <p>
                يتم إبراز الإعلانات المميزة لتكون في أعلى قائمة الإعلانات
                الظاهرة للمستخدم.
              </p>
            </div>
            <div className={css.image}>
              <Image
                alt="campaign1"
                width={150}
                height={150}
                loader={CloudfrontImageLoader}
                src={`000000000_1707660584001.png`}
              />
            </div>
          </div>
          <div className={css.benefit}>
            <div className={css.text}>
              <h4>تفاعلات أكثر</h4>
              <p>
                تحصل الإعلانات المميزة على تفاعلات (ظهور، مشاهدة، اتصالات ...)
                أكثر من الإعلانات العادية.
              </p>
            </div>
            <div className={css.image}>
              <Image
                alt="campaign2"
                width={150}
                height={150}
                loader={CloudfrontImageLoader}
                src={`000000000_1707660584000.png`}
              />
            </div>
          </div>
        </div>

        <div className={css.service}>
          <Link
            href={`/user/campaigns/new?${
              router.query?.id
                ? `step=promote&id=${router.query.id}&listing_type=${listingType}&category=${category}`
                : ""
            }`}
          >
            <Button
              theme="primary"
              data-event-target="start_sov_campaign"
              onClick={() => {
                trackStartFeaturedAdClickInteraction({
                  listingType,
                  category,
                  source: previousPath || decodeURI(document?.referrer),
                });
                if (listing) {
                  trackFeaturedAdAddToCart({
                    listingType,
                    category,
                    item: getFeaturedAdItemObject({
                      listing,
                      index: 1,
                      categoriesDictionary: {},
                      categoryName: category,
                    }),
                    planId: router.query.id,
                  });
                }
              }}
            >
              ابدأ الخدمة
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
});

Campaigns.getInitialProps = InitialResolver(AuthResolver());
export default Campaigns;
